<template>
    <div class="sd-panel" :class="{ 'visible': visible }">
        <!-- 头部工具栏 -->
        <div class="panel-header">
            <div class="left">
                <i class="el-icon-back" @click="handleBack"></i>
                <span class="title">AI生成</span>
            </div>
        </div>

        <!-- 内容区域 -->
        <div class="panel-content">
            <!-- 上传区域 -->
            <div class="upload-section">
                <div class="section-header" @click="toggleUploadSection">
                    <div class="title">
                        <i class="el-icon-circle-plus"></i>
                        <span>上传图片</span>
                    </div>
                    <i :class="['el-icon-arrow-down', {'is-active': !uploadSectionCollapsed}]"></i>
                </div>
                <div class="section-content" v-show="!uploadSectionCollapsed">
                    <div class="content-wrapper">
                        <div class="left-section">
                            <!-- 动态输入节点 -->
                            <div v-if="selectedWorkflow.inputNodes && selectedWorkflow.inputNodes.length > 0" class="dynamic-inputs">
                                <div v-for="node in showInputNodes" :key="node.nodeId" class="input-node">
                                    <!-- 文本输入 -->
                                    <div v-if="node.fieldValue === 'text'" class="text-input">
                                        <el-input
                                            type="textarea"
                                            :autosize="{ minRows: 2, maxRows: 2}"
                                            :placeholder="`请输入${node.fieldLabel||'提示词'}`"
                                            v-model="inputNodeValues[node.nodeId + '_' + node.fieldName]">
                                        </el-input>
                                    </div>
                                    
                                    <!-- 图片上传 -->
                                    <div class="image-actions" v-if="sourceImageUrl[node.nodeId]">
                                        <el-tooltip content="删除后重新上传" placement="top">
                                            <i class="el-icon-delete" @click="handleDeleteSourceImage(node.nodeId, node.fieldName)" style="color: #ffffff;"></i>
                                        </el-tooltip>
                                    </div>
                                    <div v-if="node.fieldValue === 'image'" class="image-upload">
                                        <el-upload
                                            :ref="`upload-${node.nodeId}`"
                                            class="upload-container"
                                            action=""
                                            :auto-upload="false"
                                            :show-file-list="false"
                                            :on-change="(file) => handleImageChange(file, node.nodeId, node.fieldName)"
                                            :before-upload="beforeUpload"
                                            :limit="1"
                                            :multiple="false"
                                            accept="image/*,video/mp4,image/gif">
                                            <div class="upload-area">
                                                <template v-if="!sourceImageUrl[node.nodeId]">
                                                    <div class="upload-content">
                                                        <i class="el-icon-plus"></i>
                                                        <div class="upload-text">点击上传{{ node.fieldLabel||'图片' }}</div>
                                                    </div>
                                                </template>
                                                <div v-else class="preview-container">
                                                    <div v-if="uploading[node.nodeId]" class="upload-loading">
                                                        <i class="el-icon-loading"></i>
                                                    </div>
                                                    <template v-else>
                                                        <!-- 图片预览 -->
                                                        <el-image
                                                            v-if="sourceFileType[node.nodeId] === 'image'"
                                                            class="preview-image"
                                                            :src="sourceImageUrl[node.nodeId]"
                                                            fit="contain">
                                                        </el-image>
                                                        <!-- 视频预览 -->
                                                        <video 
                                                            v-else-if="sourceFileType[node.nodeId] === 'video'"
                                                            class="preview-video"
                                                            :src="sourceImageUrl[node.nodeId]"
                                                            controls
                                                            autoplay
                                                            loop
                                                            muted>
                                                        </video>
                                                        <!-- GIF预览 (作为图片处理，但可能需要特殊样式) -->
                                                        <el-image
                                                            v-else-if="sourceFileType[node.nodeId] === 'gif'"
                                                            class="preview-image gif-preview"
                                                            :src="sourceImageUrl[node.nodeId]"
                                                            fit="contain">
                                                        </el-image>
                                                    </template>
                                                </div>
                                            </div>
                                        </el-upload>
                                    </div>
                                    
                                    <!-- 下拉选择 -->
                                    <div v-else-if="typeof node.fieldValue === 'object' && node.fieldValue.type === 'select'" class="select-input">
                                        <el-select
                                            v-model="inputNodeValues[node.nodeId + '_' + node.fieldName]"
                                            clearable
                                            :placeholder="`请选择${node.fieldLabel||''}`">
                                            <el-option
                                                v-for="option in node.fieldValue.options"
                                                :key="option.value"
                                                :label="option.label"
                                                :value="option.value">
                                            </el-option>
                                        </el-select>
                                    </div>
                                </div>
                            </div>
                            <div class="action-wrapper">
                                <div class="select-workflow">
                                    <el-select
                                        v-model="selectedWorkflowId"
                                        placeholder="请选择工作流"
                                        @change="handleWorkflowChange">
                                        <el-option
                                            v-for="item in workflows"
                                            :key="item.workflowId"
                                            :label="item.name+'[消耗'+item.price+'点]'"
                                            :value="item.workflowId">
                                        </el-option>
                                    </el-select>
                                </div>
                                <div class="button-wrapper">
                                    <el-tooltip content="生成图片" placement="top">
                                        <el-button type="warning" @click="handleRunningUp" :loading="generatingImage">运行</el-button>
                                    </el-tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 任务列表 -->
            <div class="task-list">
                <div v-for="task in tasks" :key="task.taskId" class="task-item">
                    <div class="task-header">
                        <div class="task-type-tag" :class="task.type + '-tag'">
                            {{ getWorkflowTypeLabel(task.type) }}
                        </div>
                        <span class="task-name">{{ task.name }}</span>
                        <div class="task-info">
                            <span class="task-time">{{ task.createTime }}</span>
                            <span class="task-status" :class="task.status">
                                {{ getTaskStatus(task) }}
                            </span>
                        </div>
                    </div>
                    <div class="task-images">
                        <div v-for="(image, index) in task.images" :key="index" class="image-item">
                            <template v-if="image.fileType === 'mp4'">
                                <video 
                                    :src="image.fileUrl" 
                                    class="video-preview"
                                    controls
                                    style="width: 100%; height: 120px; object-fit: cover;">
                                </video>
                            </template>
                            <template v-else>
                                <el-image 
                                    :src="image.fileUrl" 
                                    fit="cover"
                                    :preview-src-list="[image.fileUrl]">
                                </el-image>
                            </template>
                            <div class="image-item-actions">
                                <el-tooltip content="下载" placement="top">
                                    <i class="el-icon-download" @click="handleDownloadImage(image)" style="color: #ffffff;"></i>
                                </el-tooltip>
                                <el-tooltip content="保存" placement="top">
                                    <i class="el-icon-folder-add" @click="handleSaveImage(task.type, image)" style="color: #ffffff;"></i>
                                </el-tooltip>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="hasMore" class="load-more" @click="loadMoreTasks">加载更多</div>
            </div>
        </div>
        
        <!-- 保存对话框 -->
        <el-dialog 
            title="保存到" 
            class="dialog save-dialog" 
            :visible.sync="saveDialogVisible"
            width="600px"
            :modal-append-to-body="false"
            :append-to-body="true"
            :z-index="3000"
            @close="handleDialogClose">
            <div style="padding: 0 80px">
                <el-form :model="saveForm" class="body" :rules="rules" ref="saveForm" label-position="top">
                    <el-form-item label="所在文件夹" prop="folderId">
                        <el-select
                            v-model="saveForm.folderId"
                            placeholder="请选择文件夹"
                            class="folder-select"
                            style="width: 350px"
                            popper-class="folder-select-dropdown">
                            <el-option
                                v-for="item in flattenedDirectory"
                                :key="item.id"
                                :label="item.pathName"
                                :value="item.id">
                                <span class="folder-path">{{ item.pathName }}</span>
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="名称" prop="fileName">
                        <el-input 
                            v-model="saveForm.fileName" 
                            placeholder="请输入文件名"
                            :maxlength="50"
                            style="width: 350px"
                            show-word-limit>
                        </el-input>
                    </el-form-item>
                </el-form>
            </div>
            <div slot="footer" class="dialog-footer">
                <el-button size="small" @click="saveDialogVisible = false">取 消</el-button>
                <el-button size="small" type="primary" @click="submitForm" :loading="saving">确 定</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import Protocol from '@/common/scripts/protocol.js'
import EUY from '@/common/scripts/euy.js'
import { mapState } from 'vuex'

export default {
    name: 'SDUi',
    components: {
    },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: ''
        },
        teamId: {
            type: String,
            default: ''
        },
        folderId: {
            type: String,
            default: ''
        },
        workflowId: {
            type: String,
            default: ''
        }
    },
    data () {
        return {
            platform: this.$route.query.platform,
            token:EUY.GetStorage('login_status').tokenId,
            mask_visible: false,
            leftWidth: 50,
            isDragging: false,
            sourceImageUrl: {}, // 存储各个节点的图片地址 {nodeId: value}
            sourceImageName: {}, // 存储各个节点的图片名称 {nodeId: value}
            sourceFileType: {}, // 存储各个节点的图片类型 {nodeId: value} 'image', 'video', 'gif'
            sourceText: '',
            // 新增：支持多个输入节点的数据结构
            inputNodeValues: {}, // 存储各个节点的值 {nodeId: value}
            targetImageUrl: '',
            targetText: '',
            workflows: [],
            workflowIndex: -1,
            selectedWorkflow: {},
            selectedWorkflowId: '',
            uploading: {}, // 存储各个节点的上传状态 {nodeId: boolean}
            uploadLoading: false,
            showInputImage: true,
            showInputText: false,
            showOutputImage: false,
            showOutputText: false,
            pollingTimer: null,
            generatingImage: false,
            tasks: [],
            pollingInterval: null,
            pageNum: 1,
            pageSize: 10,
            loading: false,
            hasMore: true,
            uploadSectionCollapsed: true,
            
            // 新增保存对话框相关数据
            saveDialogVisible: false,
            formLabelWidth: '120px',
            saving: false,
            saveForm: {
                fileName: '',
                fileUrl: '',
                folderId: '',
                type: '',
                directory: []
            },
            rules: {
                folderId: [
                    { required: true, message: '请选择保存位置', trigger: 'change' }
                ],
                fileName: [
                    { required: true, message: '请输入文件名', trigger: 'blur' },
                    { min: 1, max: 50, message: '长度在 1 到 50 个字符', trigger: 'blur' }
                ]
            },
            flattenedDirectory: []
        }
    },
    computed: {
        showInputNodes() {
            return this.selectedWorkflow.inputNodes.filter(node => node.fieldValue === 'image' || node.fieldValue === 'text'
                || (typeof node.fieldValue === 'object' && node.fieldValue.type === 'select'));
        },
    },
    watch: {
        visible: {
            handler(newVal) {
                console.log('SDUi visible changed:', newVal)
                if (newVal) {
                    this.fetchWorkflows()
                    this.startTasksPolling()
                } else {
                    this.stopTasksPolling()
                }
            },
            immediate: true
        },
        teamId: {
            handler(newVal, oldVal) {
                console.log('SDUi teamId changed:', oldVal, '->', newVal)
                if (this.visible && newVal !== oldVal) {
                    // 清空当前任务列表
                    this.tasks = []
                    this.pageNum = 1
                    this.hasMore = true
                    // 重新获取任务列表
                    this.fetchTasks()
                }
            },
            immediate: true  // 组件创建时立即执行一次
        },
        workflowId: {
            handler(newVal, oldVal) {
                console.log('SDUi workflowId changed:', oldVal, '->', newVal)
                if (newVal && newVal !== oldVal) {
                    // 根据workflowId查找对应的工作流并设置为选中状态
                    this.selectWorkflowById(newVal)
                }
            },
            immediate: true
        }
    },
    methods: {
        handleBack() {
            this.$emit('update:visible', false);
        },

        // 修改工作流
        handleWorkflowChange(keyId) {
            // 找到选中的工作流索引
            this.workflowIndex = this.workflows.findIndex(item => item.workflowId === keyId);
            // 显示选中的工作流名称
            if (this.workflowIndex !== -1) {
                const workflow = this.selectedWorkflow = this.workflows[this.workflowIndex];
                this.targetText = workflow.name;
                
                // 初始化输入节点值
                this.initializeInputNodeValues();
                
                // 解析输入节点
                try {
                    const inputNodes = workflow.inputNodes ? workflow.inputNodes : [];
                    this.showInputImage = inputNodes.length === 0 || inputNodes.some(node => node.fieldValue === 'image');
                    this.showInputText = inputNodes.some(node => node.fieldValue === 'text');
                } catch (e) {
                    console.error('解析输入节点失败:', e);
                    this.showInputImage = true;
                    this.showInputText = false;
                }

                // 解析输出节点
                try {
                    const outputNodes = workflow.outputNodes ? workflow.outputNodes : [];
                    this.showOutputImage = outputNodes.length === 0 || outputNodes.some(node => node.fieldValue === 'image');
                    this.showOutputText = outputNodes.some(node => node.fieldValue === 'text');
                } catch (e) {
                    console.error('解析输出节点失败:', e);
                    this.showOutputImage = true;
                    this.showOutputText = false;
                }

                this.uploadSectionCollapsed = false;
            }
        },

        // 根据workflowId选择工作流
        selectWorkflowById(workflowId) {
            if (!workflowId || this.workflows.length === 0) {
                return;
            }
            
            // 查找对应的工作流
            const targetWorkflow = this.workflows.find(w => w.workflowId === workflowId);
            if (targetWorkflow) {
                // 设置选中的工作流ID并触发变更
                this.selectedWorkflowId = workflowId;
                this.handleWorkflowChange(workflowId);
            } else {
                console.warn('未找到指定的工作流:', workflowId);
            }
        },

        // 运行工作流
        async handleRunningUp() {
            if (this.workflowIndex === -1) {
                this.$message.error('请先选择工作流')
                return
            }

            const workflow = this.workflows[this.workflowIndex]
            const params = {
                workflowId: workflow.workflowId,
                nodeInfoList: []
            }

            // 添加输入参数 - 使用新的inputNodeValues数据结构
            try {
                const inputNodes = workflow.inputNodes || []
                inputNodes.forEach(node => {
                    let fieldValue = null
                    
                    const nodeKey = node.nodeId + '_' + node.fieldName
                    
                    if (node.fieldValue === 'image') {
                        // 优先使用节点特定的值
                        fieldValue = this.inputNodeValues[nodeKey]
                    } else if (node.fieldValue === 'text') {
                        // 优先使用节点特定的值
                        fieldValue = this.inputNodeValues[nodeKey]
                    } else if (node.fieldValue === 'seed') {
                        // 使用节点特定的种子值，如果没有则生成随机种子
                        fieldValue = this.generateRandomSeed()
                    } else if (typeof node.fieldValue === 'object' && node.fieldValue.type === 'select') {
                        // 下拉选择的值
                        fieldValue = this.inputNodeValues[nodeKey]
                    } else {
                        // 其他输入类型或常量
                        fieldValue = node.fieldValue
                    }
                    
                    if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
                        params.nodeInfoList.push({
                            nodeId: node.nodeId,
                            fieldName: node.fieldName,
                            fieldValue: fieldValue
                        })
                    }
                })
            } catch (e) {
                console.error('解析输入节点失败:', e)
                this.$message.error('工作流配置错误')
                return
            }

            // 如果没有有效的输入参数
            if (params.nodeInfoList.length === 0) {
                this.$message.error('请先完善输入参数')
                return
            }

            const URL = '/api/storage/storage/ai/generate-image?teamId=' + this.teamId
            const headers = {
                'Authorization': this.token,
                'Content-Type': 'application/json'
            }

            try {
                this.uploadLoading = true
                this.generatingImage = true;
                const response = await this.AJAX.post(URL, headers, JSON.stringify(params))
                if (response.code === 10000) {
                    this.$message.success('开始生成图片');
                } else {
                    throw new Error(response.message || '生成失败')
                }
            } catch (error) {
                console.error('生成失败:', error)
                this.$message.error(error.message || '生成失败，请重试')
            } finally {
                this.uploadLoading = false
                this.generatingImage = false;
            }
        },
        generateRandomSeed() {
            // 生成1到2147483647之间的随机整数（这是常见的种子范围）
            return Math.floor(Math.random() * 2147483647) + 1;
        },
        async uploadFile(formData) {
            const headers = {
                'Authorization': this.token
            };
            
            // 使用相对路径，让代理生效
            const URL = process.env.HOST + '/api/storage/storage/upload';
            
            try {
                const response = await this.AJAX.post(URL, headers, formData);
                return response;
            } catch (error) {
                console.error('文件上传失败:', error);
                throw error;
            }
        },

        beforeUpload(file) {
            console.log('beforeUpload', file);
            const isImage = file.type.startsWith('image/');
            const isVideo = file.type === 'video/mp4';
            const isGif = file.type === 'image/gif';
            const isValidFile = isImage || isVideo || isGif;
            const isLt20M = file.size / 1024 / 1024 < 20;

            if (!isValidFile) {
                this.$message.error('只能上传图片、GIF或MP4视频文件！');
                return false;
            }
            if (!isLt20M) {
                this.$message.error('文件大小不能超过 20MB！');
                return false;
            }
            return true;
        },

        async handleImageChange(file, nodeId, fieldName) {
            console.log('handleImageChange', nodeId, fieldName, file);
            if (!file || !file.raw) return;

            const nodeKey = nodeId + '_' + fieldName

            // 清除之前的状态
            this.$set(this.sourceImageUrl, nodeId, '');
            this.$set(this.sourceImageName, nodeId, '');
            this.$set(this.sourceFileType, nodeId, '');
            this.$set(this.uploading, nodeId, true);

            try {
                // 确定文件类型
                if (file.raw.type.startsWith('image/gif')) {
                    this.$set(this.sourceFileType, nodeId, 'gif');
                } else if (file.raw.type.startsWith('video/')) {
                    this.$set(this.sourceFileType, nodeId, 'video');
                } else if (file.raw.type.startsWith('image/')) {
                    this.$set(this.sourceFileType, nodeId, 'image');
                } else {
                    throw new Error('不支持的文件类型');
                }

                // 显示本地预览
                this.$set(this.sourceImageUrl, nodeId, URL.createObjectURL(file.raw));
                this.$set(this.sourceImageName, nodeId, file.name);
                
                // 构建上传数据
                const formData = new FormData();
                formData.append('file', file.raw);
                //所有上传的文件都是临时文件,使用type为nono,方便管理
                formData.append('type', 'none');
                formData.append('ownerId', this.teamId);

                const response = await this.uploadFile(formData);
                
                if (response.code === 10000) {
                    this.$set(this.inputNodeValues, nodeKey, response.datas.url);
                    this.$set(this.sourceImageUrl, nodeId, response.datas.url);
                    this.$message.success('上传成功');
                } else {
                    throw new Error(response.message || '上传失败');
                }
            } catch (error) {
                this.$message.error(error.message || '上传失败，请重试');
                this.clearUploadCache(nodeId, fieldName);
            } finally {
                this.$set(this.uploading, nodeId, false);
                //上传后清除上传组件的文件,避免二次上传失效
                const uploadRef = Array.isArray(this.$refs[`upload-${nodeId}`]) ? this.$refs[`upload-${nodeId}`][0] : this.$refs[`upload-${nodeId}`];
                if (uploadRef && uploadRef.clearFiles && typeof uploadRef.clearFiles === 'function') {
                    uploadRef.clearFiles();
                }
            }
        },

        clearUploadCache(nodeId, fieldName) {
            const nodeKey = nodeId + '_' + fieldName
            
            // 清除当前文件和预览
            this.$set(this.sourceImageUrl, nodeId, '');
            this.$set(this.sourceImageName, nodeId, '');
            this.$set(this.sourceFileType, nodeId, '');
            this.$set(this.inputNodeValues, nodeKey, '');
            
            // 清除对应节点的上传组件文件
            // ref="upload"在v-for中会被存储为数组
            const uploadRef = Array.isArray(this.$refs[`upload-${nodeId}`]) ? this.$refs[`upload-${nodeId}`][0] : this.$refs[`upload-${nodeId}`];
            
            if (uploadRef) {
                // 尝试调用clearFiles方法
                if (uploadRef.clearFiles && typeof uploadRef.clearFiles === 'function') {
                    uploadRef.clearFiles();
                }
            }
        },

        async fetchWorkflows() {
            let URL = process.env.HOST + '/api/storage/storage/ai/workflows'
            let token = this.token
            let headers = {
                'Authorization': token
            }
            let params = {
                page: 1,
                pageSize: 200,
            }

            this.AJAX.get(URL, headers, params).then((data) => {
                if (data.code === 10000) {
                    this.workflows = data.datas.rows || []
                    // 如果传入了workflowId，优先选择指定的工作流
                    if (this.workflowId && this.workflows.length > 0) {
                        const targetWorkflow = this.workflows.find(w => w.workflowId === this.workflowId)
                        if (targetWorkflow) {
                            this.selectedWorkflowId = this.workflowId
                            this.handleWorkflowChange(this.selectedWorkflowId)
                        } else {
                            // 如果指定的工作流不存在，选择第一个
                            this.selectedWorkflowId = this.workflows[0].workflowId
                            this.handleWorkflowChange(this.selectedWorkflowId)
                        }
                    } else if (this.workflows.length > 0) {
                        // 如果没有传入workflowId，自动选中第一个
                        this.selectedWorkflowId = this.workflows[0].workflowId
                        this.handleWorkflowChange(this.selectedWorkflowId)
                    }
                } else {
                    this.$message.error(data.message || '获取工作流列表失败')
                }
            }).catch((error) => {
                console.error('获取工作流列表失败:', error)
                this.$message.error('获取工作流列表失败')
            })
        },
        async fetchTasks() {
            try {
                const URL = process.env.HOST + '/api/storage/storage/ai/tasks';
                const headers = {
                    'Authorization': this.token
                };
                const params = {
                    teamId: this.teamId,
                    pageNum: this.pageNum,
                    pageSize: this.pageSize
                };
                
                const response = await this.AJAX.get(URL, headers, params);
                if (response.code === 10000) {
                    const newTasks = response.datas.rows.map(task => this.transformTaskData(task));
                    let addTasks = [];

                    // 更新现有任务或添加新任务
                    newTasks.forEach(newTask => {
                        const existingIndex = this.tasks.findIndex(t => t.taskId === newTask.taskId);
                        if (existingIndex !== -1) {
                            // 更新现有任务
                            this.tasks.splice(existingIndex, 1, newTask);
                        } else {
                            addTasks.push(newTask);
                        }
                    });

                    this.tasks = [...addTasks, ...this.tasks];
                    this.hasMore = this.tasks.length < response.datas.total;
                    if(this.hasMore) this.pageNum++;
                }
            } catch (error) {
                console.error('Error fetching tasks:', error);
            }
        },
        transformTaskData(task) {
            let images = [];
            if (task.result && Array.isArray(task.result)) {
                images = task.result.map(item => ({
                    fileUrl: item.fileUrl,
                    fileType: item.fileType
                }));
            }
            
            return {
                taskId: task.taskId,
                name: task.workflowName,
                type: task.workflowType,
                status: task.status,
                images: images,
                createTime: new Date(task.createdTime * 1000).toLocaleString(),
                expireTime: task.expireTime
            };
        },
        async loadMoreTasks() {
            if (this.loading || !this.hasMore) return;
            
            try {
                this.loading = true;
                const URL = process.env.HOST + '/api/storage/storage/ai/tasks';
                const headers = {
                    'Authorization': this.token
                };
                const params = {
                    teamId: this.teamId,
                    pageNum: this.pageNum,
                    pageSize: this.pageSize
                };
                
                const response = await this.AJAX.get(URL, headers, params);
                if (response.code === 10000) {
                    const newTasks = response.datas.rows.map(task => this.transformTaskData(task));
                    
                    // 获取当前列表最后pageSize条记录
                    const lastTasks = this.tasks.slice(-this.pageSize);
                    
                    // 比较新任务和最后的任务，只添加不重复的任务
                    const uniqueNewTasks = newTasks.filter(newTask => 
                        !lastTasks.some(lastTask => lastTask.taskId === newTask.taskId)
                    );
                    
                    if (uniqueNewTasks.length > 0) {
                        this.tasks = [...this.tasks, ...uniqueNewTasks];
                    }

                    this.hasMore = this.tasks.length < response.datas.total;
                    if(this.hasMore) this.pageNum++;
                }
            } catch (error) {
                console.error('Error loading more tasks:', error);
            } finally {
                this.loading = false;
            }
        },
        startTasksPolling() {
            this.fetchTasks(); // 立即获取一次
            this.pollingInterval = setInterval(() => {
                this.fetchTasks();
            }, 10000); // 每10秒更新一次
        },
        stopTasksPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },
        async handleSaveImage(type, image) {
            this.saveForm.fileName = `未命名`;
            this.saveForm.fileUrl = image.fileUrl;
            this.saveForm.type = type;
            
            // 获取文件夹列表后显示对话框
            await this.getFolderList(type, this.teamId);
            this.saveDialogVisible = true;
        },
        async handleSaveSourceImage(type, imageUrl, fileName) {
            console.log("handleSaveSourceImage type=====", type, imageUrl, fileName);
            // 处理文件名：去掉扩展名，为空则使用"未命名"
            let name = '未命名';
            if (fileName) {
                const lastDotIndex = fileName.lastIndexOf('.');
                name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
            }
            this.saveForm.fileName = name;
            this.saveForm.fileUrl = imageUrl;
            this.saveForm.type = type;
            
            // 获取文件夹列表后显示对话框
            await this.getFolderList(type, this.teamId);
            this.saveDialogVisible = true;
        },
        async handleDeleteSourceImage(nodeId, fieldName) {
            console.log("handleDeleteSourceImage nodeId====", nodeId, "fieldName====", fieldName);
            // 清除当前文件和预览
            this.clearUploadCache(nodeId, fieldName);
            this.$set(this.uploading, nodeId, false);
        },
        
        async getFolderList(type, team_id) {
            try {
                const URL = process.env.HOST + '/pro/api/ResourceFolder/folderTree';
                const headers = {
                    'token': this.token,
                };
                
                const formData = new FormData();
                formData.append('type', type);
                formData.append('team_id', team_id); 
                
                const response = await this.AJAX.post(URL, headers, formData);
                if (response.code === 0) { 
                    this.saveForm.directory = response.result;
                    // 将树形结构扁平化，并添加完整路径名
                    this.flattenedDirectory = this.flattenDirectoryTree(response.result);
                    
                    // 设置默认选中第一个值
                    if (this.flattenedDirectory && this.flattenedDirectory.length > 0) {
                        this.$nextTick(() => {
                            this.saveForm.folderId = this.flattenedDirectory[0].id;
                        });
                    }
                } else {
                    throw new Error(response.msg || '获取文件夹列表失败');
                }
            } catch (error) {
                console.error('获取文件夹列表失败:', error);
                this.$message.error(error.message || '获取文件夹列表失败，请重试');
            }
        },

        flattenDirectoryTree(tree, parentPath = '') {
            let result = [];
            tree.forEach(node => {
                const currentPath = parentPath ? `${parentPath} / ${node.label}` : node.label;
                result.push({
                    id: node.id,
                    label: node.label,
                    pathName: currentPath
                });
                if (node.children && node.children.length > 0) {
                    result = result.concat(this.flattenDirectoryTree(node.children, currentPath));
                }
            });
            return result;
        },
        
        handleDialogClose() {
            // this.$refs.saveForm?.resetFields();
            // this.saving = false;
        },
        
        submitForm() {
            this.$refs.saveForm.validate(async (valid) => {
                if (valid) {
                    await this.confirmSaveImage();
                }
            });
        },
        
        async confirmSaveImage() {
            if (this.saving) return;
            
            this.saving = true;
            try {
                const URL = process.env.HOST + '/api/storage/storage/resource/v1/save';
                const formData = new FormData();
                formData.append('type', this.saveForm.type);
                formData.append('teamId', this.teamId); 
                formData.append('folderId', this.saveForm.folderId); 
                formData.append('fileUrl', this.saveForm.fileUrl); 
                formData.append('title', this.saveForm.fileName);
                
                const headers = {
                    'Authorization': this.token
                };

                const response = await this.AJAX.post(URL, headers, formData);
                
                if (response.code === 10000) {
                    this.$message.success('保存成功');
                    this.saveDialogVisible = false;
                } else {
                    throw new Error(response.message || '保存失败');
                }
            } catch (error) {
                this.$message.error(error.message || '保存失败，请重试');
            } finally {
                this.saving = false;
            }
        },
        async handleDownloadImage(image) {
            try {
                // 显示下载中状态
                this.$message.info('正在准备下载...');
                
                // 获取文件内容
                const response = await fetch(image.fileUrl);
                if (!response.ok) {
                    throw new Error('下载失败');
                }
                
                // 获取文件blob
                const blob = await response.blob();
                
                // 创建下载链接
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                const fileName = image.fileUrl.split('/').pop();
                link.download = fileName;
                
                // 添加到DOM并触发下载
                document.body.appendChild(link);
                link.click();
                
                // 清理资源
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                this.$message.success('下载完成');
            } catch (error) {
                console.error('下载失败:', error);
                this.$message.error('下载失败，请重试');
            }
        },
        handleScroll() {
            const scrollHeight = document.documentElement.scrollHeight;
            const scrollTop = document.documentElement.scrollTop;
            const clientHeight = document.documentElement.clientHeight;
            
            // 当距离底部100px时加载更多
            if (scrollHeight - scrollTop - clientHeight < 100) {
                this.loadMoreTasks();
            }
        },
        toggleUploadSection() {
            this.uploadSectionCollapsed = !this.uploadSectionCollapsed;
        },
        getWorkflowTypeLabel(type) {
            const typeMap = {
                'scene': '场景',
                'prop': '道具',
                'material': '形象'
            };
            return typeMap[type] || type;
        },
        getTaskStatus(task) {
            if (task.status === 0) {
                return '生成中';
            }else if (task.status === 2) {
                return '任务失败';
            }
            
            if (task.expireTime) {
                const now = Math.floor(Date.now() / 1000);
                const daysLeft = Math.ceil((task.expireTime - now) / (24 * 60 * 60));
                return `${daysLeft}天后过期`;
            }
            
            return '已完成';
        },
        
        // 处理节点图片上传
        async handleNodeImageChange(file, nodeId) {
            console.log('handleNodeImageChange', file, nodeId);
            if (!file || !file.raw) return;

            try {
                // 构建上传数据
                const formData = new FormData();
                formData.append('file', file.raw);
                formData.append('type', 'none'); // 临时文件
                formData.append('ownerId', this.teamId);

                const response = await this.uploadFile(formData);
                
                if (response.code === 10000) {
                    // 更新对应节点的值
                    this.$set(this.inputNodeValues, nodeId, response.datas.url);
                    this.$message.success('上传成功');
                } else {
                    throw new Error(response.message || '上传失败');
                }
            } catch (error) {
                this.$message.error(error.message || '上传失败，请重试');
            }
        },
        
        // 删除节点图片
        deleteNodeImage(nodeId, fieldName) {
            const nodeKey = nodeId + '_' + fieldName
            this.$set(this.inputNodeValues, nodeKey, '');
        },
        
        // 初始化输入节点值
        initializeInputNodeValues() {
            if (!this.selectedWorkflow.inputNodes) return;
            
            this.selectedWorkflow.inputNodes.forEach(node => {
                const nodeKey = node.nodeId + '_' + node.fieldName
                if (!this.inputNodeValues.hasOwnProperty(nodeKey)) {
                    let defaultValue = '';
                    
                    if (node.fieldValue === 'seed') {
                        defaultValue = this.generateRandomSeed();
                    } else if (typeof node.fieldValue === 'object' && node.fieldValue.type === 'select') {
                        // 下拉选择默认选中第一个选项
                        if (node.fieldValue.defaultValue) {
                            defaultValue = node.fieldValue.defaultValue;
                        }
                    }
                    
                    this.$set(this.inputNodeValues, nodeKey, defaultValue);
                }
            });
        }
    },
    mounted () {
        let _this = this
        let  platform = this.$route.query.platform
        // ACTION
        // 模块初始化
        window[TOOLBAR_NAME] = {}
        window[TOOLBAR_NAME].init = () => {
            this.token = window[TOOLBAR_NAME].token
        }
        // 素材市场选择素材后触发
        window[TOOLBAR_NAME].message = (senderId, packageId, sessionId, messageId, protoBytes) => {
            // 素材市场选择素材后触发
        }
        // 不需要在mounted中启动轮询，因为watch visible会处理
        window.addEventListener('scroll', this.handleScroll);
    },
    beforeDestroy() {
        this.stopTasksPolling();
        if (this.pollingTimer) {
            clearTimeout(this.pollingTimer);
        }
        this.generatingImage = false;
        window.removeEventListener('scroll', this.handleScroll);
    }
}
</script>

<style lang="stylus" scoped>
/* 视频预览样式 */
.preview-video
    width: 100%
    height: 100%
    object-fit: contain

/* GIF预览特殊样式 */
.gif-preview
    width: 100%
    height: 100%

.sd-panel
    position: fixed
    top: 0
    right: -100%
    width: 500px
    height: 100vh
    background: #212121
    transition: right 0.3s ease-in-out
    z-index: 1000
    display: flex
    flex-direction: column

    &.visible
        right: 0

    .panel-header
        height: 60px
        padding: 0 20px
        background: #303030
        display: flex
        align-items: center
        justify-content: space-between

        .left
            display: flex
            align-items: center
            
            i
                font-size: 20px
                color: #fff
                cursor: pointer
                margin-right: 10px
                
            .title
                color: #fff
                font-size: 16px

        .right
            display: flex
            align-items: center
            gap: 10px

    .panel-content
        flex: 1
        overflow-y: auto
        padding: 20px

        .upload-section
            margin-bottom: 20px
            background: #fff
            border-radius: 8px
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)
            
            .section-header
                padding: 10px 15px
                display: flex
                justify-content: center
                align-items: center
                cursor: pointer
                border-bottom: 1px solid #eee
                position: relative
                
                .title
                    font-size: 14px
                    font-weight: 500
                    display: flex
                    align-items: center
                    gap: 8px
                    
                    i
                        font-size: 16px
                        color: #409EFF
                
                i.el-icon-arrow-down
                    position: absolute
                    right: 15px
                    transition: transform 0.3s
                    
                    &.is-active
                        transform: rotate(180deg)
            
            .section-content
                padding: 10px
                padding-top: 5px
                
                .content-wrapper
                    display: flex
                    gap: 20px
                    margin-top: 5px
                    
                    .left-section
                        display: flex
                        flex-direction: column
                        gap: 5px
                        align-items: center
                        width: 100%
                        
                        // 动态输入节点容器
                        .dynamic-inputs
                            width: 90%
                            display: flex
                            flex-direction: column
                            gap: 10px
                            
                            .input-node
                                display: flex
                                flex-direction: column
                                gap: 10px
                                align-items: center
                                .text-input
                                    width: 100%
                                .select-input
                                    width: 100% 
                                    .el-select
                                        width: 100%
                                .image-actions
                                    display: flex
                                    gap: 10px
                                    background: rgba(0, 0, 0, 0.5)
                                    padding: 5px 10px
                                    border-radius: 4px
                                    
                                    i
                                        font-size: 16px
                                        cursor: pointer
                                        color: #ffffff
                                        
                                        &:hover
                                            color: #409EFF
                                
                                .image-upload
                                    width: 150px
                                    height: 150px
                                    margin: 0 auto
                                    
                                    .upload-container
                                        width: 100%
                                        height: 100%
                                        
                                        .upload-area
                                            width: 100%
                                            height: 100%
                                            border: 1px dashed #d9d9d9
                                            border-radius: 6px
                                            display: flex
                                            justify-content: center
                                            align-items: center
                                            transition: all 0.3s
                                            
                                            &:hover
                                                border-color: #409EFF
                                                
                                            .upload-content
                                                display: flex
                                                flex-direction: column
                                                justify-content: center
                                                align-items: center
                                                gap: 10px
                                                width: 150px
                                                height: 150px
                                                
                                                .el-icon-plus
                                                    font-size: 28px
                                                    color: #8c939d
                                                
                                                .upload-text
                                                    font-size: 12px
                                                    color: #8c939d
                                        
                                        .preview-container
                                            width: 150px
                                            height: 150px
                                            position: relative
                                            
                                            .upload-loading
                                                width: 100%
                                                height: 100%
                                                display: flex
                                                justify-content: center
                                                align-items: center
                                                background: rgba(255,255,255,0.8)
                                                
                                                .el-icon-loading
                                                    font-size: 24px
                                                    color: #409EFF
                                            
                                            .preview-image
                                                width: 100%
                                                height: 100%
                                                display: block
                                            &:hover
                                                .image-actions
                                                    opacity: 1
                        .action-wrapper
                            width: 90%
                            display: flex
                            justify-content: space-between
                            align-items: center
                            margin-top: 10px
                            gap: 10px
                            .select-workflow
                                width: 100% 
                                .el-select
                                    width: 100%          
                            .button-wrapper
                                .el-button
                                    width: 56px
                                    height: 28px
                                    padding: 0
                                    display: flex
                                    align-items: center
                                    justify-content: center
        .task-list
            .task-item
                margin-bottom: 20px
                border: 1px solid #e4e7ed
                border-radius: 4px
                overflow: hidden

                .task-header
                    padding: 12px
                    background: #f5f7fa
                    display: flex
                    align-items: center
                    justify-content: space-between
                    margin-bottom: 10px

                    .task-type-tag
                        padding: 2px 8px
                        border-radius: 4px
                        font-size: 12px
                        margin-right: 8px
                        color: #fff
                        
                        &.scene-tag
                            background-color: #67C23A
                        
                        &.prop-tag
                            background-color: #409EFF
                        
                        &.material-tag
                            background-color: #E6A23C

                    .task-name
                        flex: 1
                        font-size: 14px
                        color: #333
                        margin-right: 10px

                    .task-info
                        display: flex
                        align-items: center
                        gap: 10px
                        
                        .task-time
                            color: #909399
                            font-size: 12px

                        .task-status
                            font-size: 12px
                            padding: 2px 6px
                            border-radius: 10px
                            
                            &.0
                                color: #409EFF
                                background: rgba(64,158,255,0.1)
                            
                            &.1
                                color: #67C23A
                                background: rgba(103,194,58,0.1)

                .task-images
                    padding: 20px
                    background-color: #202528
                    border-radius: 8px
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)
                    display: grid
                    grid-template-columns: repeat(2, 1fr)
                    gap: 12px

                    .image-item
                        position: relative
                        overflow: hidden
                        border-radius: 4px
                        background-color: #fdfdfd
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1)

                        .el-image
                            width: 100%
                            height: 120px
                            display: block
                            object-fit: cover
                        
                        .image-item-actions
                            position: absolute
                            top: 10px
                            right: 10px
                            display: flex
                            gap: 10px
                            opacity: 0
                            transition: opacity 0.3s
                            background: rgba(0, 0, 0, 0.5)
                            padding: 5px 10px
                            border-radius: 4px
                            
                            i
                                font-size: 16px
                                cursor: pointer
                                color: #ffffff
                                transition: color 0.3s
                                
                                &:hover
                                    color: #409EFF
                        
                        &:hover
                            .image-item-actions
                                opacity: 1
        .load-more
            text-align: center
            padding: 10px
            cursor: pointer
            color: #409eff

    .dialog
        .body
            padding: 10px 20px
            
            .folder-select
                width: 100%
            
        .dialog-footer
            text-align: right
            padding: 10px 20px
            
    // 添加到 <style> 标签外
    /deep/ .folder-select-dropdown
        .el-select-dropdown__item
            padding-right: 20px
            .folder-path
                color: #909399
                font-size: 12px
</style>



// WEBPACK FOOTER //
// SDUi.vue?35e4cfdb